<template>
    <app-page-error v-if="showErrorPage" />
    
    <div v-else id="projectContainer" class="is-clipped" :class="isEmbed ? 'lb-embed-height' : 'lb-full-height'" >
        <div class="columns is-marginless">

            <div class="zone-content" :class="contentClasses">
                <div class="columns is-multiline px-5" v-if="!isMobile">
                    <div class="column mt-4 is-4 is-offset-8"
                        v-if="project.mode != 'map' && project.type != 'treasurehunt'">
                        <app-search-bar :placeholder="$t('search_text')" :filter.sync="typeFilter" :text.sync="searchValue"
                            class="mt-3" @input="searchObjects"/>
                    </div>
                </div>
                <div class="columns is-multiline" :class="!isMap?'px-5':'pl-3 pt-3'" v-if="!isMobile">
                    <component 
                        :is="getComponent" 
                        ref="content" 
                        :project-id="projectId" 
                        :items="filteredItems"
                        :draft="contentDraft" 
                        :modal-opened="showInputModal" 
                        :show-answers="showingAnswers"
                        :editionEnabled="false" 
                        :creationEnabled="false" 
                        :canEdit="false"
                        :pointDraft="pointDraft" 
                        :mapPositionActive="mapPositionActive" 
                        :activeObject="activeObject"
                        :highlightedMarker="highlightedMarker" 
                        :selectedObjectMarker="selectedObjectMarker"
                        :selectedObject="selectedObject" 
                        :sessionStarted="sessionStarted" 
                        :sessionPaused="sessionPaused"
                        :isViewMode="true"
                        class="column is-12 is-paddingless content-section" 
                        @show="showObject"
                        @highlightMarker="highlightMarker"
                        @zoomImage="zoomImage"
                    />
                </div>
            </div>

            <router-view />

            <l-project-modal-session-creation v-if="startingSession" @close="startingSession = false"
                @sessionActivation="activateSession" />

            <div v-if="showSimulateSessionModal" class="is-clipped">
                <div class="modal-background has-scroll-y">
                    <component :is="simulatePlayComponent" @close="showSimulateSessionModal = false" />
                </div>
            </div>

            <l-project-modal-configuration-changes v-if="configurationChangesWarning && isProjectAdmin"
                @toggle-configuration="openConfiguration" />

            <project-session-details-modal v-if="showSessionDetails" @close="showSessionDetails = false" />

        </div>

        <!-- <l-project-object-preview
            :object="objectToView"
            v-if="showPreviewModal"
            @close="showPreviewModal = false"
        />-->
        <l-project-view-modal-content :object="activeObject" v-if="showObjectModal"
            @close="closeObjectModal" :view-mode="true" @zoomImage="zoomImage"></l-project-view-modal-content>

        <TreasurehuntPreview :objects="pathPreview" :startInObject="initialPreviewObject" :tileProvider="mapProvider"
            v-if="showTreasurehuntSessionPreview && pathPreview" @close="hideTreasurehuntPreview"
            class="treasurehunt-preview"></TreasurehuntPreview>

        <l-project-view-object-content :object="objectToPreview" v-if="showPreviewModal" :viewMode="true"
            @close="closeObjectPreviewModal" @zoomImage="zoomImage"></l-project-view-object-content>
        
        <image-view-modal :url="imageToView" v-if="viewImageModal" @close="closeImageModal"/>

    </div>
</template>

<script>
import ProjectHeader from '@/components/ProjectHeader';
import ProjectContent from '@/components/ProjectContent';
import ProjectContentMapV2 from '@/components/ProjectContentMapV2';
import ProjectConfiguration from '@/components/ProjectConfiguration';
import ProjectConfigurationMenu from '@/components/ProjectConfigurationMenu';
import ProjectSessionCreationModal from '@/components/ProjectSessionCreationModal';
import AppPageError from '@/components/AppPageError';
import AppSearchBar from '@/components/AppSearchBar';
import PlayQuiz from '@/components/PlayQuiz';
import store from '@/store/index';
import { Document } from 'flexsearch';
import _ from 'underscore';
import { isEmptyObject } from '@/lib/helpers';
import moment from 'moment';
import Swal from 'sweetalert2';
import { fetchItem, pauseSession, continueSession, stopSession } from '@/services/API';
import LProjectModalSessionCreation from '@/components/LProjectModalSessionCreation';
import LProjectModalConfigurationChanges from '@/components/LProjectModalConfigurationChanges';
import ProjectSessionDetailsModal from '@/components/ProjectSessionDetailsModal';
import LProjectViewModalContent from '@/components/LProjectViewModalContent';
import ProjectContentTreasureHunt from '@/components/ProjectContentTreasureHunt';
import TreasurehuntPreview from '@/components/TreasurehuntPreview';
import { TILE_PROVIDERS, DEFAULT_TILE_PROVIDER } from '@/lib/constants';
import LProjectViewObjectContent from '@/components/LProjectViewObjectContent';
import ImageViewModal from '@/components/ImageViewModal';

export default {
    name: 'ProjectView',
    components: {
        ProjectHeader,
        ProjectContent,
        ProjectConfiguration,
        ProjectSessionCreationModal,
        AppPageError,
        AppSearchBar,
        LProjectModalSessionCreation,
        LProjectModalConfigurationChanges,
        ProjectSessionDetailsModal,
        ProjectContentMapV2,
        ProjectConfigurationMenu,
        LProjectViewModalContent,
        ProjectContentTreasureHunt,
        TreasurehuntPreview,
        LProjectViewObjectContent,
        ImageViewModal,
    },
    /*beforeRouteUpdate(to, from, next) {
        if (to.params.edit == true) {
            this.editObject(_.findWhere(this.items, { _id: to.params.object }));
        }
        next((vm) => {
            console.log('finished route update');
            vm.$Progress.finish();
        });
    },
    beforeRouteLeave(to, from, next) {
        const hasChanges = this.$refs.configuration
            ? this.$refs.configuration.hasChanges
            : false;
        if (
            hasChanges &&
            !confirm(this.$t('project.discard_unsaved_changes'))
        ) {
            next(false);
        } else {
            next();
        }
    },*/
    props: {
        projectId: { type: String, default: undefined },
    },
    metaInfo() {
        // if no subcomponents specify a metaInfo.title, this title will be used
        return {
            title: this.project.title,
            titleTemplate: '%s - eduloc360',
            meta: [
                // Twitter Card
                { name: 'twitter:card', content: 'summary' },
                { name: 'twitter:title', content: this.project.title },
                {
                    name: 'twitter:description',
                    content: this.project.description,
                },
                // image must be an absolute path
                { name: 'twitter:image', content: this.projectBackgroundImage },
                // Facebook OpenGraph
                { property: 'og:title', content: this.project.title },
                { property: 'og:site_name', content: 'eduloc360' },
                { property: 'og:type', content: 'website' },
                { property: 'og:image', content: this.projectBackgroundImage },
                {
                    property: 'og:description',
                    content: this.project.description,
                },
            ],
        };
    },
    data() {
        return {
            searchValue: '',
            typeFilter: this.$t('Constants.FILTER_OPTION_ALL'),
            searchDropdownOptions: [
                this.$t('Constants.FILTER_OPTION_ALL'),
                this.$t('Constants.FILTER_OPTION_MINE'),
                this.$t('Constants.FILTER_OPTION_MENTIONED'),
                this.$t('Constants.FILTER_OPTION_COMMENTED'),
                this.$t('Constants.FILTER_OPTION_LIKED'),
            ],
            selectedItem: {},
            contentDraft: { owner: {} },
            creationEnabled: false,
            creating: false,
            searchIndex: new Document({
                resolution: 3,
                tokenize: 'full',
                document: {
                    id: '_id',
                    index: [
                        {
                            field: 'title',
                            minlength: 0,
                        },
                        {
                            field: 'description',
                            minlength: 0,
                        },
                        {
                            field: 'tags',
                            minlength: 0,
                        },
                        'owner:name',
                        'owner:email',
                    ],
                },
            }),
            eventSource: undefined,
            showSettings: false,
            showSessions: false,
            showCreateSessionModal: false,
            showSessionDetailModal: false,
            showMapCreation: false,
            showInputModal: false,
            componentType: undefined,
            fromClipboard: false,
            items: this.$store.getters.projectContent || [],
            logo: process.env.ROOT_PATH + require('@/assets/images/logo.png'),
            showErrorPage: false,
            renderMap: true,
            showingAnswers: false,
            showSimulateSessionModal: false,
            session: {},
            originalItem: {},
            showNotifications: false,

            sessionStarted: false,
            sessionPaused: false,
            startingSession: false,
            editionEnabled: false,
            showThreadList: false,
            showHistory: false,

            configurationChangesWarning: false,
            showSessionActive: false,
            showListSessions: false,
            showSessionDetails: false,

            embed: this.$route.meta.embed,
            hideLogin: this.$route.meta.hideLogin,

            pointDraft: undefined,
            mapPositionActive: false,
            showObjectList: false,
            showObjectModal: false,
            activeObject: {},

            objectType: undefined,
            previousObject: undefined,
            selectedObject: "",
            hasDelete: false,
            itemForSimulation: [],
            initialPreviewObject: '',
            pathPreview: {},
            highlightedMarker: undefined,
            selectedObjectMarker: undefined,
            showTreasurehuntSessionPreview: false,

            tileProviders: TILE_PROVIDERS,
            defaultTileProvider: DEFAULT_TILE_PROVIDER,

            showPreviewModal: false,
            objectToPreview: {},
            viewImageModal: false,
            imageToView: "",
            bellowLimit: false,

            showShare: false,

        };
    },
    computed: {
        mapProvider() {
            if (this.project.mapProvider) {
                return _.findWhere(this.tileProviders, {
                    name: this.project.mapProvider,
                });
            }
            // console.log("defaultProvider",this.defaultTileProvider)
            return this.defaultTileProvider;
        },
        showTreasurehuntHelp() {
            // console.log("showTreasurehuntHelp",this.project.format=="PROJECT_TYPE_TREASUREHUNT" && this.items.length<2 && this.hasCreationPermission)
            return this.project.format == "PROJECT_TYPE_TREASUREHUNT" && this.items.length < 2 && this.hasCreationPermission;
        },
        contentClasses() {
            //let classesToAdd = "default-theme";
            //if (this.isMobile) {
                let classesToAdd = "default-theme-full";
            //}
            if (this.hideProjectMenu) {
                classesToAdd += " no-menu"
            }
            return classesToAdd;
        },
        hideProjectMenu() {
            return !this.canCreateObject && !this.showObjectList && (this.project.format == "PROJECT_TYPE_WALL" || ((this.project.format == "PROJECT_TYPE_QUIZ" || this.project.format == "PROJECT_TYPE_QUIZHUNT" || this.project.format == "PROJECT_TYPE_TREASUREHUNT") && !this.isProjectAdmin));
        },
        hasLocation() {

            return this.project.format == "PROJECT_TYPE_MAP" || this.project.format == "PROJECT_TYPE_GEOQUIZ" || this.objectType == 'clue'
        },
        menuClasses() {
            let classes = "zindex10 lb-project-menu lb-full-height has-scroll-y";
            if (this.isMobile) {
                classes = "tabs is-centered project-menu";
            }
            if (this.embed) {
                classes += " embed-menu";
            }
            return classes;
        },
        isMobile() {
            return this.$mq == "mobile"
        },
        showMenu() {
            //return this.$store.getters['Project/showMenu'];
            return this.creationEnabled || this.editionEnabled || this.showSettings || this.showThreadList || this.showHistory || this.showListSessions || this.showSessionActive || this.showObjectList || this.showShare;
        },
        hasObjects() {
            if (this.$store.getters.projectContent.length) {
                return true;
            }
            return false;
        },
        contentModel() {
            //console.log(this.projectType);
            if (this.objectType) {
                switch (this.objectType) {
                    case 'object':
                        return this.$store.getters.objectModel;
                    case 'question':
                        return this.$store.getters.questionModel;
                    case 'clue':
                        // console.log("clue model")
                        return this.$store.getters.clueGoalModel;
                }
            } else {
                if (this.projectType === 'trivial' || this.projectType === 'quizhunt') {
                    // console.log(this.$store.getters.questionModel);
                    if (this.project.mode == "map") {
                        return this.$store.getters.geoQuestionModel;
                    } else {
                        return this.$store.getters.questionModel;
                    }

                } else if (this.project.mode == 'map') {
                    return this.$store.getters.markerModel;
                } else {
                    return this.$store.getters.objectModel;
                }
            }

        },
        projectType() {
            return this.$store.getters.project.type;
        },
        isProjectAdmin() {
            return (
                this.$store.getters.isAdmin ||
                this.project.accessLevel == 'administrator'
            );
        },
        canCreateObject() {
            return this.isAnnonymousParticipation || (!this.isAnonymous && (this.project.accessLevel != 'read' && (!this.sessionStarted || (this.sessionStarted && this.sessionPaused))));
        },
        isAnonymous() {
            return this.$store.getters.isAnonymous;
        },
        isAnnonymousParticipation() {
            if (this.project.allowAnonymous == true) {
                return this.project.accessLevel != 'read' && this.$store.getters.isAnonymous
            }
            return false;
        },
        canEditObject() {
            return this.editionEnabled && (!this.sessionStarted || (this.sessionStarted && this.sessionPaused));
        },
        isEditionBlocked() {
            return !this.sessionStarted || (this.sessionStarted && this.sessionPaused);
        },
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        },
        project() {
            return this.$store.getters.project;
        },
        itemsSource() {
            return this.$store.getters.projectContent;
        },
        projectBackgroundImage() {
            return this.$store.getters.projectImage || this.logo;
        },
        cardsComponent() {
            return ProjectContent;
        },
        mapComponent() {
            return ProjectContentMapV2;
        },
        treasurehuntComponent() {
            return ProjectContentTreasureHunt;
        },
        getComponent() {
            if (this.project.mode == 'map') {
                return this.mapComponent;
            }
            if (this.project.type == 'treasurehunt') {
                return this.treasurehuntComponent
            }
            return this.cardsComponent;
        },
        
        filteredItems() {
            let items;
            this.searchIndex.remove(this.items);
            switch (this.typeFilter) {
                case this.$t('Constants.FILTER_OPTION_MINE'):
                    items = this.items.filter((item) => {
                        return (
                            item.owner.owner == this.$store.getters.user.owner
                        );
                    });
                    break;
                case this.$t('Constants.FILTER_OPTION_COMMENTED'):
                    items = this.items.filter((item) => {
                        return _.some(item.comments, (comment) => {
                            return (
                                comment.owner.owner ==
                                this.$store.getters.user.owner
                            );
                        });
                    });
                    break;
                case this.$t('Constants.FILTER_OPTION_MENTIONED'):
                    items = this.items.filter((item) => {
                        return _.some(item.comments, (comment) => {
                            if (!comment.message) {
                                return false;
                            }
                            return (
                                comment.message.indexOf(
                                    '@' + this.$store.getters.user.username
                                ) > -1
                            );
                        });
                    });
                    break;
                case this.$t('Constants.FILTER_OPTION_LIKED'):
                    items = this.items.filter((item) => {
                        return item.liked;
                    });
                    break;
                default:
                    items = this.items;
            }
            if (this.searchValue) {
                this.searchIndex.add(items);
                const result = this.searchIndex.search(this.searchValue);
                const ids = _.chain(result).pluck('result').flatten().value();
                return this.items.filter((item) => {
                    return _.contains(ids, item._id);
                });
            }
            return items;
        },
        hasCreationPermission() {
            return false;
        },
        hasEditionPermission() {
            return false;
        },
        hasAdminPermission() {
            return false;
        },
        isTrivial() {
            return this.project.type == 'trivial';
        },
        isEmbed() {
            if(this.$route.meta){
                if(this.$route.meta.embed){
                    return true;
                }else{
                    return false;
                }
            }
            return false;
        },
        isMap() {
            return this.project.format=="PROJECT_TYPE_MAP"
        },
        isGame(){
            if(this.project.format == 'PROJECT_TYPE_QUIZ' || this.project.format == 'PROJECT_TYPE_GEOQUIZ' || this.project.format == 'PROJECT_TYPE_QUIZHUNT' || this.project.format == 'PROJECT_TYPE_TREASUREHUNT' || this.project.format == 'PROJECT_TYPE_MEMORY'){
                return true;
            }else if(this.project.type=='trivial' || this.project.type=="treasurehunt"){
                return true;
            }
            return false;

        },
    },
    watch: {
        items: {
            handler: function () {
                this.items.forEach((item) => {
                    this.searchIndex.add(item);
                });
                //this.searchIndex.add(this.items)
            },
            immediate: true,
        },
        itemsSource() {
            this.items = this.itemsSource;
        },
        project: {
            handler: function () {
                if (this.project.activeSession) {
                    // console.log("active session ID",this.project.activeSession)
                    if (!this.session || (this.session._id != this.project.activeSession)) {
                        // console.log("getting session")
                        /*if (
                            !this.isLoggedIn &&
                            (this.project.privacy == 'secret' ||
                                this.project.privacy == 'public') 
                        ) {
                            // console.log('dispatching anonymous login');
                            this.$store
                                .dispatch('anonymousLogin', 'anonymous')
                                .then(() => {
                                    // console.log(this.$store.getters.user);
                                    this.fetchSession();
                                });
                        } else {
                            this.fetchSession();
                        }*/
                       this.fetchSession();

                    }

                }
            },
            immediate: true,
        },
        
    },
    mounted: function () {
        /*setTimeout(() => {
            // this.$Progress.start();
        }, 500);*/
        store
            .dispatch('fetchProject', this.$route.params.projectId)
            .then(() => {
                // console.log("project fetched",this.project)

                store
                    .dispatch('fetchProjectContent', {
                        _id: this.$route.params.projectId,
                    })
                    .catch(() => {
                        console.error('failed to fetch project content');
                    })
                    .finally(() => {
                        // this.$Progress.finish();
                    });

                /*if ( // desactivat el login anònim
                    !this.isLoggedIn &&
                    (this.project.privacy == 'secret' ||
                        this.project.privacy == 'public') &&
                    this.project.allowAnonymous
                ) {
                    // console.log('dispatching anonymous login');
                    this.$store
                        .dispatch('anonymousLogin', 'anonymous')
                        .then(() => {
                            // console.log(this.$store.getters.user);
                        });
                }*/

                if (!this.project.format) {
                    this.project.format = 'PROJECT_TYPE_WALL';
                    if (this.project.type == 'collection' && !this.project.mode) {
                        this.project.format = 'PROJECT_TYPE_WALL';
                    } else if (this.project.type == 'collection' && this.project.mode == 'map') {
                        this.project.format = 'PROJECT_TYPE_MAP';
                    } else if (this.project.type == 'trivial' && this.project.mode == 'map') {
                        this.project.format = 'PROJECT_TYPE_GEOQUIZ';
                    } else if (this.project.type == 'trivial' && !this.project.mode) {
                        this.project.format = 'PROJECT_TYPE_QUIZ';
                    } else if (this.project.type == 'treasurehunt') {
                        this.project.format = 'PROJECT_TYPE_TREASUREHUNT';
                    } else if (this.project.type == 'storytelling') {
                        this.project.format = 'PROJECT_TYPE_STORYTELLING';
                    } else if (this.project.type == 'timemachine') {
                        this.project.format = 'PROJECT_TYPE_TIMEMACHINE';
                    } else if (this.project.type == 'scape') {
                        this.project.format = 'PROJECT_TYPE_SCAPE';
                    }
                    this.configurationChangesWarning = true;
                }

                if (this.isGame) {
                    this.showErrorPage = true;
                }
            })
            .catch((e) => {
                console.error('failed to fetch project', e.status || e);
                this.showErrorPage = true;
            });




        window.addEventListener('paste', this.handlePaste);
        const url = new URL(process.env.VUE_APP_MERCURE_URL);
        url.searchParams.append(
            'topic',
            'http://project.' + this.$route.params.projectId
        );
        this.eventSource = new EventSource(url);
        this.eventSource.onmessage = this.onChangeReceived;
    },
    beforeDestroy() {
        this.eventSource.close();
        window.removeEventListener('paste', this.handlePaste);
    },
    destroyed() {
        this.$store.dispatch('cleanProject');
        if (
            this.$store.getters.isAnonymous &&
            (this.project.privacy == 'secret' ||
                this.project.privacy == 'public') &&
            this.project.allowAnonymous
        ) {
            this.$store.dispatch('logout');
        }
    },

    methods: {
        zoomImage(url){
            this.imageToView=url;
            this.viewImageModal=true;
        },
        closeImageModal(){
            this.imageToView="";
            this.viewImageModal=false;
        },
        closeObjectPreviewModal(){
            this.showPreviewModal = false;
            this.objectToPreview = {};
        },
        showObjectPreview(e){
            // console.log(e)
            this.objectToPreview = e;
            this.showPreviewModal = true;
        },
        highlightMarker(e) {
            // console.log(e)
            this.highlightedMarker = e;
        },
        
        
        
        
        addPoint(pointData) {
            // console.log("adding point",typeof(pointData.lat))
            if(typeof pointData.lat === 'string'){
                this.contentDraft.location = {
                    latitude: parseFloat(pointData.lat),
                    longitude: parseFloat(pointData.lon),
                };
                // console.log("transform to float")
            }else{
                this.contentDraft.location = {
                    latitude: pointData.lat,
                    longitude: pointData.lon,
                };
            }
            
            this.pointDraft = {
                location: { latitude: pointData.lat, longitude: pointData.lon },
            };
            // console.log(this.pointDraft)
        },
        
        
        
        
        toggleObjectList() {
            // this.creationEnabled || this.editionEnabled || this.showSettings || this.showThreadList || this.showHistory
            if (this.creationEnabled) {
                Swal.fire({
                    //title: this.$t('delete_object_title'),
                    text: this.$t('creation_object_active'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('userProfile.leave_school_continue'),
                    cancelButtonText: this.$t('userProfile.leave_school_cancel'),
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.cancelInput();
                        this.showObjectList = true;
                    }
                });
            } else if (this.editionEnabled) {
                Swal.fire({
                    //title: this.$t('delete_object_title'),
                    text: this.$t('edition_object_active'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('userProfile.leave_school_continue'),
                    cancelButtonText: this.$t('userProfile.leave_school_cancel'),
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.cancelInput();
                        this.showObjectList = true;
                    }
                });
            } else if (this.showThreadList) {
                this.showThreadList = false;
                this.showObjectList = true;
            } else if (this.showHistory) {
                this.showHistory = false;
                this.showObjectList = true;
            } else if (this.showListSessions) {
                this.showListSessions = false;
                this.showObjectList = true;
            } else if (this.showSessionActive) {
                this.showSessionActive = false;
                this.showObjectList = true;
            } else if (this.showSettings) {
                this.showSettings = false;
                this.showObjectList = true;
            } else if(this.showShare){
                this.showShare = false;
                this.showObjectList = true;
            }else if (this.showObjectList) {
                this.searchValue = '';
                this.filteredItems;
                this.showObjectList = false;
            } else {
                this.showObjectList = true;
            }
        },
        
        
        
        searchObjects(text) {
            this.searchValue = text;
            this.filteredItems;
        },
        
        onChangeReceived(e) {
            const data = JSON.parse(e.data);
            switch (data.type) {
                case 'UPDATE':
                case 'NEW_COMMENT':
                    this.$store.commit('UPDATE_OBJECT', data.object);
                    break;
                case 'CREATE':
                    this.$store.commit('ADD_OBJECT_TO_PROJECT', data.object);
                    break;
                case 'NEW_PROJECT_COMMENT':
                    this.$store.commit('ADD_COMMENT_TO_PROJECT', data.object);
                    break;
                case 'PROJECT_COMMENT_UPDATED':
                    this.$store.commit('UPDATE_COMMENT_TO_PROJECT', data.object);
                    break;
                case 'LIKE_PROJECT_COMMENT':
                    this.$store.commit('UPDATE_COMMENT', {
                        _id: data.object._id,
                        comment: data.object,
                    });
                    break;
                case 'NEW_PROJECT_CHANGE':
                    this.$store.commit('ADD_CHANGE_TO_PROJECT', data.object);
                    break;
                case 'DELETE_OBJECT':
                    this.$store.commit('DELETE_OBJECT', data.object);
                    break;
                case 'NEW_ACTIVE_SESSION':
                    this.$store.commit('ADD_ACTIVE_SESSION_TO_PROJECT', data.object);
                    if (this.project.activeSession && !this.session.length) {
                        this.fetchSession();
                    }
                    break;
                case 'PAUSE_ACTIVE_SESSION':
                    if (this.session && !this.session.isPaused) {
                        this.fetchSession();
                    }
                    break;
                case 'CONTINUE_ACTIVE_SESSION':
                    if (this.session && this.session.isPaused) {
                        this.fetchSession();
                    }

                    break;
                case 'REMOVE_ACTIVE_SESSION':
                    this.$store.commit('REMOVE_ACTIVE_SESSION_TO_PROJECT');
                    if (this.session) {
                        this.session = {};
                        this.$store.dispatch('cleanSession');
                    }

                    break;
                default:
                    console.log('unhandled event ', data.type || data.message);
            }
        },
        handlePaste(e) {
            this.$store.dispatch('setClipboardContent', e.clipboardData.items);
        },

        async deleteObject(object) {
            // console.log('try to remove object', object);
            if (this.project.type == 'treasurehunt' || this.project.format == "PROJECT_TYPE_TREASUREHUNT") {
                this.deleteTreasurehuntObject(object);
            } else {

                if (!object._id || object._id == 'draft') {
                    return this.cancelInput();
                } else {

                    Swal.fire({
                        title: this.$t('delete_object_title'),
                        text: this.$t('delete_object_confirmation_message'),
                        showCancelButton: true,
                        confirmButtonText: this.$t('userProfile.leave_school_continue'),
                        cancelButtonText: this.$t('userProfile.leave_school_cancel'),
                    }).then((result) => {
                        if (result.isConfirmed) {
                            this.$store.dispatch('deleteObject', object._id);
                            this.items = this.$store.getters.projectContent.filter(
                                function (item) {
                                    return item['_id'] != 'draft' && item['_id'] != object._id;
                                }
                            );
                            this.cancelInput();
                        }
                    });



                }
            }
        },

        openConfiguration() {
            this.configurationChangesWarning = false;
            this.toggleSettings();
        },
        showObject(object) {
            this.activeObject = object;
            this.showObjectModal = true;
        },
        closeObjectModal() {
            this.showObjectModal = false;
            this.activeObject = {};
        },
        
        centerMap(object) {
            this.$refs.content.$refs.editMap.fitBounds(
                L.latLngBounds([[object.location.latitude, object.location.longitude]]), { 'maxZoom': 19 }
            );
            this.showObject(object);
        },
        
        

    },
};
</script>

<style scoped lang="css">
@import '../assets/css/project.css';
@import '../assets/css/map.css';
@import '../assets/css/tippyedulocblue.css';
@import '../assets/css/swal-eduloc.css';

#projectContainer /deep/ .map-edit-container .leaflet-container{
    height: calc(100vh - 8rem) !important;
}

</style>
